import { Paper, Typography } from "@mui/material"
import { useTheme } from "@mui/material/styles"
import _times from "lodash/times"
import React from "react"

import styled, { css, keyframes } from "styled-components"
import { isValidColor } from "../../utils/color"

const starEntranceAnim = keyframes`
  0% {
    opacity: 0.2;
    transform: scale(1);
  }
  80% {
    transform: scale(1.2);
    opacity: 1;
  }
  100% {
    transform: scale(1);
  }
`

const StarsWrap = styled.div``

const StarsList = styled.ul`
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: row;
`

const StarWrap = styled(Paper)`
  height: 16px;
  width: 16px;
  display: inline-flex;
  margin: 0 2px 0 0;
  padding: 0;
  position: relative;
`

const animCSS = css`
  animation: ${starEntranceAnim} 1s 1s forwards;
`

const StarFilled = styled.span<{ animated: boolean; color: string }>`
  background: ${({ color }) => color};
  border: 1px solid rgba(0, 0, 0, 0.2);
  box-shadow: 0 0 3px ${({ color }) => color}, inset 0 0 3px rgba(0, 0, 0, 0.15);
  position: absolute;
  width: 100%;
  height: 100%;
  transform: scale(1);
  transition: all 0.4s ease;

  ${({ animated }) => (animated ? animCSS : "")}

  &:hover {
    transform: scale(1.2);
  }
`

const StarUnfilled = styled.span<{ color: string }>`
  background: ${({ color }) => color};
  border: 1px solid rgba(0, 0, 0, 0.2);
  position: absolute;
  width: 4px;
  height: 1px;
  left: 50%;
  top: 50%;
  margin-left: -2px;
`

const StarsContent = styled.div`
  display: flex;
  flex-direction: row;
`

const TitleWrap = styled.div`
  white-space: nowrap;
`

const TotalWrap = styled.div<{ color: string }>`
  padding-left: 0.5rem;

  > span {
    color: ${({ color }) => color};
  }
`

interface IStarsProps {
  /**
   * Enable entrance animation
   * @default true
   */
  animated?: boolean
  /**
   * The component color
   * @default
   */
  color?: string
  /**
   * The max number of stars
   * @default 5
   */
  max?: number
  /**
   * Show the total value as text
   * @default false
   */
  showTotal?: boolean
  /**
   * A small title to show above the stars
   * @default ""
   */
  title?: string
  /**
   * The number of active stars
   */
  value: React.ReactText
}

const Stars = ({
  animated = true,
  color,
  showTotal = false,
  title = "",
  value,
  max = 5
}: IStarsProps) => {
  const theme = useTheme()
  if (value && value > max) {
    return <div>Value cannot be &gt; max</div>
  }

  let activeColor = theme.palette.primary.light
  if (color && isValidColor(color)) {
    activeColor = color
  } else if (
    color &&
    /primary|secondary/.test(color) &&
    color in theme.palette
  ) {
    activeColor = theme.palette[color].light
  }
  const filledStarProps = { color: activeColor }
  const unfilledStarProps = { color: theme.colorScales.bg(1).css() }
  return (
    <StarsWrap>
      {title ? (
        <TitleWrap>
          <Typography variant="overline">{title}</Typography>
        </TitleWrap>
      ) : (
        ""
      )}
      <StarsContent>
        <StarsList>
          {_times(max, i => (
            <StarWrap key={`star-wrap-${i}`}>
              {i < value ? (
                <StarFilled
                  animated={animated}
                  style={{ animationDelay: animated ? `${i * 80}ms` : "0" }}
                  {...filledStarProps}
                />
              ) : (
                <StarUnfilled {...unfilledStarProps} />
              )}
            </StarWrap>
          ))}
        </StarsList>
        {showTotal ? (
          <TotalWrap {...filledStarProps}>
            <span>{value}</span> / {max}
          </TotalWrap>
        ) : (
          ""
        )}
      </StarsContent>
    </StarsWrap>
  )
}

export default Stars
